import ActionButton from "@/components/buttons/ActionButton";
import AntIcon from "@/components/icons/AntIcon";
import { rangePickerFormItemProps, selectTagsStandardProps } from "@/utils/componentUtils";
import { getDatePickerDefaultRanges, getDateTimePickerFormat, skipInitialFormValues } from "@/utils/formUtils";
import { URLQuery } from "@/utils/queryUtils";
import { validationConstants, validations } from "@/utils/validationUtils";
import { Button, Card, DatePicker, Flex, Form, Input, Select } from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { rowGutter } from "../../../../../common/constants";
import { Permission } from "../../../../../common/security/enums";
import { hasAllPermissions, hasPermission } from "../../../../../common/security/utils";
import { usePermissions } from "../../../../auth/hooks";
import { CalcType, calcTypeTMap } from "../../../enums";
import { DEFAULT_FILTER_CALC_RECORDS_QUERY_PARAMS } from "../../api";
import { OperationType, operationTypeTMap } from "../../enums";
import { CalcRecordFilterPageRequest } from "../../types";
import { CalcTypeTag } from "../CalcTypeTag";
import { OperationTypeTag } from "../OperationTypeTag";

interface ExtendedCalcRecordFilterPageRequest extends CalcRecordFilterPageRequest {
  createdAtRange?: string[];
}

interface Props {
  searchParams: CalcRecordFilterPageRequest;
}

export const CalcRecordsListFilterView = ({ searchParams }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [form] = Form.useForm<ExtendedCalcRecordFilterPageRequest>();

  const permissions = usePermissions();

  useEffect(() => {
    form.setFieldsValue({
      calcType: searchParams.calcType,
      operationType: searchParams.operationType,
      keyword: searchParams.keyword,
      createdAtRange:
        searchParams.createdAtMin && searchParams.createdAtMax
          ? [searchParams.createdAtMin, searchParams.createdAtMax]
          : undefined,
    });
  }, [searchParams, form]);

  const calcTypeOptions = useMemo(() => {
    const calcTypes: CalcType[] = [];

    if (hasPermission(permissions, Permission.ADMIN_MTPL_CALCULATORS)) {
      calcTypes.push(CalcType.MTPL);
    }
    if (hasPermission(permissions, Permission.ADMIN_CRASH_CALCULATORS)) {
      calcTypes.push(CalcType.CRASH);
    }
    if (hasAllPermissions(permissions, [Permission.ADMIN_MTPL_CALCULATORS, Permission.ADMIN_CRASH_CALCULATORS])) {
      calcTypes.push(CalcType.MTPL_CRASH);
    }
    if (hasPermission(permissions, Permission.ADMIN_CRASH_CALCULATORS)) {
      calcTypes.push(CalcType.GAP, CalcType.PAS);
    }
    if (hasPermission(permissions, Permission.ADMIN_REALTY_CALCULATORS)) {
      calcTypes.push(CalcType.REALTY);
    }
    if (hasPermission(permissions, Permission.ADMIN_TRAVEL_CALCULATORS)) {
      calcTypes.push(CalcType.TRAVEL);
    }

    return calcTypes;
  }, [permissions]);

  const handleFilterSubmit = ({ createdAtRange, ...values }: ExtendedCalcRecordFilterPageRequest) => {
    const filter: CalcRecordFilterPageRequest = {
      ...values,
      createdAtMin: createdAtRange?.[0],
      createdAtMax: createdAtRange?.[1],
    };

    navigate(
      {
        search: URLQuery.searchParamsToString({
          searchParams: {
            ...filter,
            ...skipInitialFormValues(filter, DEFAULT_FILTER_CALC_RECORDS_QUERY_PARAMS),
            pageIndex: undefined,
          },
        }),
      },
      { replace: true },
    );
  };

  const handleFilterClear = () => {
    form.resetFields();
    handleFilterSubmit(form.getFieldsValue());
  };

  return (
    <Card
      className="card-box"
      title={<h2>{t("calc.records.titles.list")}</h2>}
      extra={
        <ActionButton color="danger" size="middle" variant="outlined" icon="reload" onClick={handleFilterClear}>
          {t("common.resetFilter")}
        </ActionButton>
      }
    >
      <Form form={form} layout="vertical" onFinish={handleFilterSubmit} name="calcRecordsListFilterForm">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "5fr 3fr 3fr 6fr",
            gap: rowGutter,
            alignItems: "end",
          }}
          className="min-width-0-children"
        >
          <Form.Item
            name="keyword"
            label={t("common.searchKeyword")}
            rules={[
              validations.size(
                validationConstants.SEARCH_KEYWORD_MIN_LENGTH,
                validationConstants.SEARCH_KEYWORD_MAX_LENGTH,
              ),
            ]}
          >
            <Input allowClear placeholder={t("calc.records.helpers.searchUserHint")} />
          </Form.Item>

          <Form.Item
            name="operationType"
            label={t("calc.records.enums.operationType._label")}
            rules={[validations.none]}
          >
            <Select
              {...selectTagsStandardProps(operationTypeTMap)}
              allowClear
              options={Object.keys(OperationType).map((type) => ({
                value: type,
                label: <OperationTypeTag type={OperationType[type as OperationType]} />,
              }))}
            />
          </Form.Item>

          <Form.Item name="calcType" label={t("calc.enums.calcType._label")} rules={[validations.none]}>
            <Select
              {...selectTagsStandardProps(calcTypeTMap)}
              allowClear
              options={calcTypeOptions.map((type) => ({
                value: type,
                label: <CalcTypeTag type={type} />,
              }))}
            />
          </Form.Item>

          <Form.Item<ExtendedCalcRecordFilterPageRequest>
            name="createdAtRange"
            label={t("calc.records.filter.createdAtRange")}
            rules={[validations.none]}
            {...rangePickerFormItemProps("time")}
          >
            <DatePicker.RangePicker
              style={{ width: "100%" }}
              format={getDateTimePickerFormat()}
              showTime
              maxDate={dayjs().endOf("day")}
              presets={getDatePickerDefaultRanges({ hasToday: true, maxDate: dayjs().endOf("day") })}
              placeholder={[t("common.from"), t("common.to")]}
            />
          </Form.Item>
        </div>

        <Flex justify="end">
          <Button type="primary" htmlType="submit" icon={<AntIcon type="search" />}>
            {t("common.filter")}
          </Button>
        </Flex>
      </Form>
    </Card>
  );
};
